import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import cs from 'classnames';
import {
  Button,
  CloseIcon,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@forgeglobal/design-framework';
import { useStyles } from './style';
import { FormHelperText } from '@mui/material';
import { fileTypeFromStream } from 'file-type';
import { useLogger } from '../../../hooks/logger/LoggerProvider';

const MAX_FILE_SIZE_IN_MB = 50; // in MB
const MAX_FILE_SIZE = MAX_FILE_SIZE_IN_MB * 1024 * 1024; // in bytes
const MAX_FILES_AMOUNT = 10;
const MAX_FILE_NAME_LENGTH = 128;

enum UPLOAD_ERRORS {
  TOO_MANY_FILES = 'too-many-files',
  FILE_SIZE = 'file-too-large',
  FILE_TYPE = 'file-invalid-type',
  FILE_INTEGRITY = 'file-integrity',
  FILE_NAME = 'file-name',
  ILLEGAL_CHARACTERS = 'illegal-file-name',
}

const UPLOAD_ERRORS_TEXT = {
  [UPLOAD_ERRORS.FILE_SIZE]: {
    title: 'File size is too big',
    description: `File size limit exceeded. Try uploading a file that is smaller than ${MAX_FILE_SIZE_IN_MB} MB.`,
  },
  [UPLOAD_ERRORS.TOO_MANY_FILES]: {
    title: 'Maximum files uploaded',
    description: `Maximum number of files allowed is ${MAX_FILES_AMOUNT}`,
  },
  [UPLOAD_ERRORS.FILE_TYPE]: {
    title: 'Wrong file format',
    description:
      'You have chosen a file format we do not support. Please upload pdf, jpeg, heic, png, or tif file.',
  },
  [UPLOAD_ERRORS.FILE_INTEGRITY]: {
    title: 'Corrupt file',
    description:
      'You have chosen a file which is corrupt. Please upload a valid file.',
  },
  [UPLOAD_ERRORS.FILE_NAME]: {
    title: 'Lengthy file name',
    description: `File name must be ${MAX_FILE_NAME_LENGTH} or fewer characters.`,
  },
  [UPLOAD_ERRORS.ILLEGAL_CHARACTERS]: {
    title: 'Illegal Characters',
    description:
      'File name may only contain alphabets, numbers, dashes and underscores.',
  },
};

const Dropzone = ({ input, meta, accountNumber }) => {
  const classes = useStyles();
  const logger = useLogger();
  const [files, setFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const isInvalid = meta.touched && meta.invalid;
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg', '.png'],
      'image/tiff': [],
      'image/heic': [],
      'application/pdf': [],
    },
    onDrop: async (acceptedFiles, fileRejections) => {
      if (fileRejections.length) {
        const errors = fileRejections
          .map((file) => file.errors)
          .flat()
          .map((error) => error.code);
        const uniqueErrors = [...new Set(errors)].filter((error) =>
          Object.values(UPLOAD_ERRORS).includes(error as UPLOAD_ERRORS)
        );

        // ensure to many files shown first
        if (uniqueErrors.includes(UPLOAD_ERRORS.TOO_MANY_FILES)) {
          return setUploadError(UPLOAD_ERRORS.TOO_MANY_FILES);
        }

        if (uniqueErrors.length) {
          return setUploadError(uniqueErrors[0]);
        }
      }

      if (acceptedFiles.length + files.length > MAX_FILES_AMOUNT) {
        logger.logInfo(
          `Attempt to upload too many files ( ${
            acceptedFiles.length + files.length
          } ) for ${accountNumber}`
        );
        return setUploadError(UPLOAD_ERRORS.TOO_MANY_FILES);
      }

      for (const file of acceptedFiles) {
        var extension = getExtension(file.name);
        var identifiedFileType = await fileTypeFromStream(file.stream());
        if (
          identifiedFileType == null ||
          extension != identifiedFileType.ext.toLowerCase()
        ) {
          logger.logInfo(`File ${file.name} for ${accountNumber} is invalid`);
          return setUploadError(UPLOAD_ERRORS.FILE_INTEGRITY);
        }

        if (file.name.length > MAX_FILE_NAME_LENGTH) {
          logger.logInfo(
            `File ${file.name.substring(0, 10)}... with length ${
              file.name.length
            } for ${accountNumber} exceeded file name length limit`
          );
          return setUploadError(UPLOAD_ERRORS.FILE_NAME);
        }

        if (containsIllegalCharacters(file.name)) {
          logger.logInfo(
            `File ${file.name} for ${accountNumber} contains illegal characters`
          );
          return setUploadError(UPLOAD_ERRORS.ILLEGAL_CHARACTERS);
        }
      }

      const uploadedFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );

      const allFiles = [...files, ...uploadedFiles];
      setFiles(allFiles);
      if (input.onChange) {
        input.onChange(allFiles);
      }
    },
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES_AMOUNT,
  });

  const containsIllegalCharacters = (filename) => {
    const illegalChars = /[<>:"/\\|?*]/;
    return illegalChars.test(filename);
  };

  function getExtension(filename) {
    const lastDotIndex = filename.lastIndexOf('.');

    if (lastDotIndex === -1) {
      return null; // No extension found
    }

    return filename.substring(lastDotIndex + 1).toLowerCase();
  }

  const removeFile = (file) => () => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);
    setFiles(newFiles);
  };

  const thumbs = files.map((file) => (
    <div className={classes.preview} key={file.preview}>
      <div className={classes.previewInfo}>
        <Typography
          variant="body2"
          color="textPrimary"
          className={classes.fileName}>
          {file.name}
        </Typography>
        <div>{+Math.round(file.size / 1024).toFixed(2)}KB</div>
        <IconButton
          aria-label="close"
          className={classes.removeButton}
          onClick={removeFile(file)}>
          <CloseIcon className={classes.icon} color="inherit" />
        </IconButton>
      </div>

      <div className={classes.loader}>
        {/* upload status */}
        <div className={classes.loaderInner} style={{ width: '100%' }} />
      </div>
    </div>
  ));

  const closeDialog = () => {
    setUploadError('');
  };

  const dialog = (
    <Dialog open={!!uploadError} transitionDuration={0}>
      <DialogTitle onClose={closeDialog}>
        {UPLOAD_ERRORS_TEXT[uploadError]?.title}
      </DialogTitle>
      <DialogContent className={classes.dialogWrapper}>
        <div className={classes.content}>
          <Typography variant="body1" className={classes.dialogText}>
            {UPLOAD_ERRORS_TEXT[uploadError]?.description}
          </Typography>
          <Button
            color="primary"
            onClick={closeDialog}
            className={classes.dialogButton}>
            OK
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    },
    [files]
  );

  return (
    <>
      <div className={classes.wrapper}>
        <div
          {...getRootProps({
            className: cs(classes.dropzone, {
              [classes.error]: isInvalid,
              [classes.dragActive]: isDragActive,
            }),
          })}>
          <input {...getInputProps()} />
          <>
            <Typography
              variant="body1"
              className={classes.mainText}
              align="center">
              <span className={classes.desktopOnly}>Drag & drop or </span>
              <span className={classes.browseLink}>Browse</span> your files
            </Typography>
            <Typography variant="caption" color="textSecondary" align="center">
              (Only pdf, jpeg, heic, png, and tif file with max size of{' '}
              {MAX_FILE_SIZE_IN_MB} MB)
            </Typography>
          </>
        </div>
        {isInvalid && (
          <FormHelperText variant="filled" error={isInvalid}>
            {meta.error}
          </FormHelperText>
        )}
        <div className={classes.thumbsContainer}>{thumbs}</div>
      </div>
      {!!uploadError && dialog}
    </>
  );
};

export default Dropzone;
